/*----------------------------------
- TLD PAGES: PERSONA CARD
----------------------------------*/

/**
 * The persona card of a TLD page on the marketing layer (the mock's `wp-cta` card, website-tld-insight.html): the shared
 * persona heading and body, then the rebuilt `PersonaPair`, the one pair a rebuilt page renders (never a copy of its
 * tracking). Markup rather than the `PersonaBand` part: that part draws its own buttons from `MarketingAction`s, and the
 * pair's hrefs, triggers and `persona-auth-cta` hooks are `PersonaPair`'s (the frozen conversion path). The hub and
 * every insight page draw this one card.
 *
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0062
 * @rule One persona card for every TLD page: the same words (`TLD_PAGE_CONTENT.personas`) and markup, each page passing
 *       its own triggers, section, surface and signup attribution; the pair keeps `persona-auth-cta`, `data-persona` and
 *       `data-attribution`.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { Card } from '@uniquedomains/components/kit/card/Card';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';

import { TLD_HUB_CONTENT } from '@/client/catalogs/landing/tld/hubContent';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import PersonaPair from '@/client/pages/landing/components/conversion/PersonaPair';

/*----------------------------------
- TYPES
----------------------------------*/

type TldPersonasCardProps = {
    testId: string;
    /** Signup attribution of both CTAs (`User.desiredFrom`). */
    attribution: string;
    investorTriggerId: ConversionTriggerId;
    founderTriggerId: ConversionTriggerId;
    trackingPage: string;
    section: string;
    surface: string;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldPersonasCard({ testId, trackingPage, ...pair }: TldPersonasCardProps) {
    const content = TLD_PAGE_CONTENT.personas;
    return (
        <Card pad="xl" class="wp-cta" data-testid={testId}>
            <div>
                <h2>{content.heading}</h2>
                <p>{content.body}</p>
            </div>
            <PersonaPair {...pair} page={trackingPage} labels={TLD_HUB_CONTENT.personaCtas} class="wp-acts" />
        </Card>
    );
}
